import { useStorage } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

export function rgbToRgbaString(rgb: string, alpha = 1) {
  return rgb.replace(')', `, ${alpha})`).replace('rgb', 'rgba');
}

/**
 * Known color keys that are commonly used in the theme system.
 * Use with ThemeColors for type-safe access to common colors.
 */
export type KnownColorKey =
  // Gray scale
  | 'white'
  | 'gray-light'
  | 'gray-medium-light'
  | 'gray-medium'
  | 'gray-medium-dark'
  | 'gray-dark'
  | 'black'
  // Primary colors
  | 'red'
  | 'pink'
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'teal'
  | 'blue'
  | 'blue-medium'
  | 'purple'
  // Special
  | 'transparent'
  | 'current'
  | 'gray'
  | 'primary';

/**
 * Type for the appColors object - maps color keys to RGB strings.
 * Colors are discovered dynamically from CSS at runtime.
 * Use KnownColorKey for type-safe access to common colors.
 */
export type ThemeColors = Record<string, string>;

/**
 * Contrast type for themes - determines if text should be light or dark.
 */
export type ContrastType = 'light' | 'dark';

/**
 * Theme option discovered from CSS.
 */
export interface ThemeOption {
  id: string;
  name: string;
  category: string;
  contrast: ContrastType;
  order: number;
  swatches: string[]; // Array of rgb() color strings for preview
}

/**
 * Grouped themes by category.
 */
export interface ThemeCategoryGroup {
  category: string;
  themes: ThemeOption[];
}

/**
 * Discovers all theme IDs from the stylesheet by finding :root[data-theme='X'] selectors.
 * This makes tailwind.css the single source of truth for available themes.
 */
function discoverThemeIdsFromStylesheet(): string[] {
  if (typeof document === 'undefined') {
    return ['light', 'dark']; // SSR fallback
  }

  const themeIds: string[] = [];
  const themeRegex = /:root\[data-theme=['"]([^'"]+)['"]\]/;

  for (let i = 0; i < document.styleSheets.length; i++) {
    const sheet = document.styleSheets[i];
    try {
      const rules = sheet.cssRules;
      for (let j = 0; j < rules.length; j++) {
        const rule = rules[j];
        if (rule instanceof CSSStyleRule) {
          const match = rule.selectorText.match(themeRegex);
          if (match && match[1] && !themeIds.includes(match[1])) {
            themeIds.push(match[1]);
          }
        }
      }
    } catch {
      // Cross-origin stylesheets will throw SecurityError - skip them
    }
  }

  return themeIds.length > 0 ? themeIds : ['light', 'dark'];
}

/**
 * All theme colors to display as swatches in theme picker.
 */
const SWATCH_COLOR_KEYS = [
  // Gray scale
  'white',
  'gray-light',
  'gray-medium-light',
  'gray-medium',
  'gray-medium-dark',
  'gray-dark',
  'black',
  // Colors
  'red',
  'pink',
  'orange',
  'yellow',
  'lime',
  'green',
  'teal',
  'blue',
  'blue-medium',
  'purple',
];

/**
 * Reads a single CSS color variable and converts to rgb() format.
 */
function readCssColorValue(
  computedStyle: CSSStyleDeclaration,
  colorKey: string
): string {
  const value = computedStyle.getPropertyValue(`--tw-color-${colorKey}`).trim();
  if (!value) return 'rgb(128, 128, 128)';
  const parts = value.split(' ');
  if (parts.length === 3) {
    return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  }
  return value;
}

/**
 * Reads theme metadata from CSS variables for a specific theme.
 * Temporarily applies the theme to read its variables, then restores the original.
 */
function readThemeMetadata(themeId: string): ThemeOption {
  if (typeof document === 'undefined') {
    return {
      id: themeId,
      name: themeId.charAt(0).toUpperCase() + themeId.slice(1),
      category: 'Standard',
      contrast: themeId === 'light' ? 'light' : 'dark',
      order: 0,
      swatches: [],
    };
  }

  // Store current theme
  const currentTheme = document.documentElement.getAttribute('data-theme');

  // Temporarily apply the theme to read its metadata
  document.documentElement.setAttribute('data-theme', themeId);

  // Force style recalculation
  const computedStyle = getComputedStyle(document.documentElement);

  // Read metadata variables (strip quotes from CSS string values)
  const name =
    computedStyle
      .getPropertyValue('--theme-name')
      .trim()
      .replace(/^['"]|['"]$/g, '') ||
    themeId.charAt(0).toUpperCase() + themeId.slice(1);
  const category =
    computedStyle
      .getPropertyValue('--theme-category')
      .trim()
      .replace(/^['"]|['"]$/g, '') || 'Standard';
  const contrastValue =
    computedStyle
      .getPropertyValue('--theme-contrast')
      .trim()
      .replace(/^['"]|['"]$/g, '') || 'dark';
  const orderValue = computedStyle.getPropertyValue('--theme-order').trim();
  const order = orderValue ? parseInt(orderValue, 10) : 0;

  // Read swatch colors while theme is applied
  const swatches = SWATCH_COLOR_KEYS.map((key) =>
    readCssColorValue(computedStyle, key)
  );

  // Restore original theme
  if (currentTheme) {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }

  return {
    id: themeId,
    name,
    category,
    contrast: contrastValue === 'light' ? 'light' : 'dark',
    order,
    swatches,
  };
}

/**
 * Discovers all --tw-color-* CSS variables from the document's stylesheets.
 * This makes tailwind.css the single source of truth for color definitions.
 */
function discoverColorKeysFromStylesheet(): string[] {
  if (typeof document === 'undefined') {
    return [];
  }

  const colorKeys: string[] = [];

  for (let i = 0; i < document.styleSheets.length; i++) {
    const sheet = document.styleSheets[i];
    try {
      const rules = sheet.cssRules;
      for (let j = 0; j < rules.length; j++) {
        const rule = rules[j];
        // Match :root with or without data-theme attribute (e.g., :root, :root[data-theme="light"])
        if (
          rule instanceof CSSStyleRule &&
          rule.selectorText.startsWith(':root')
        ) {
          for (let k = 0; k < rule.style.length; k++) {
            const prop = rule.style[k];
            if (prop.startsWith('--tw-color-')) {
              const colorName = prop.replace('--tw-color-', '');
              if (!colorKeys.includes(colorName)) {
                colorKeys.push(colorName);
              }
            }
          }
        }
      }
    } catch {
      // Cross-origin stylesheets will throw SecurityError - skip them
    }
  }

  return colorKeys;
}

/**
 * Reads a CSS variable from the document root and converts it to rgb() format.
 * CSS variables are defined as space-separated RGB values (e.g., "255 255 255").
 */
function getCssColorVariable(name: string): string {
  if (typeof document === 'undefined') {
    return 'rgb(0, 0, 0)';
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--tw-color-${name}`)
    .trim();
  if (!value) {
    return 'rgb(0, 0, 0)';
  }
  // Convert "255 255 255" to "rgb(255, 255, 255)"
  const parts = value.split(' ');
  if (parts.length === 3) {
    return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  }
  return value;
}

// Cache discovered data - these don't change at runtime
let cachedColorKeys: string[] | null = null;
let cachedThemes: ThemeOption[] | null = null;

// Shared ref that triggers rebuilding colors when theme changes.
// Must be module-level so all useThemedColors() instances share it.
const colorsVersion = ref(0);

/**
 * Clears the theme cache, forcing re-discovery on next access.
 * Useful when themes are added dynamically (e.g., user-created themes).
 */
export function clearThemeCache(): void {
  cachedColorKeys = null;
  cachedThemes = null;
  colorsVersion.value++;
}

/**
 * Discovers and caches all available themes from CSS.
 */
function discoverThemes(): ThemeOption[] {
  if (cachedThemes !== null) {
    return cachedThemes;
  }

  const themeIds = discoverThemeIdsFromStylesheet();
  cachedThemes = themeIds.map((id) => readThemeMetadata(id));

  // First sort by theme order to determine category order from lowest-ordered theme
  cachedThemes.sort((a, b) => a.order - b.order);

  // Build category order based on first appearance (now sorted by theme order)
  const categoryOrder = new Map<string, number>();
  cachedThemes.forEach((theme) => {
    if (!categoryOrder.has(theme.category)) {
      categoryOrder.set(theme.category, categoryOrder.size);
    }
  });

  // Final sort: by category order, then by theme order within category
  cachedThemes.sort((a, b) => {
    const catOrderA = categoryOrder.get(a.category) ?? 0;
    const catOrderB = categoryOrder.get(b.category) ?? 0;
    if (catOrderA !== catOrderB) {
      return catOrderA - catOrderB;
    }
    return a.order - b.order;
  });

  return cachedThemes;
}

/**
 * Creates a reactive colors object that reads from CSS variables.
 * Color keys are discovered dynamically from the stylesheet on first call.
 * This is called when the theme changes to rebuild the colors map with current values.
 */
function buildColorsFromCssVariables(): ThemeColors {
  // Discover color keys from CSS on first call
  if (cachedColorKeys === null) {
    cachedColorKeys = discoverColorKeysFromStylesheet();
  }

  const colors: ThemeColors = {
    transparent: 'transparent',
    current: 'currentColor',
    // Gray is a special case - use gray-medium as the base gray
    gray: getCssColorVariable('gray-medium'),
  };

  for (const key of cachedColorKeys) {
    colors[key] = getCssColorVariable(key);
  }

  return colors;
}

export function useThemedColors() {
  // Discover themes from CSS. Reactive to colorsVersion so clearThemeCache() makes
  // newly added themes visible without a full reload.
  const availableThemes = computed<ThemeOption[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    colorsVersion.value;
    return discoverThemes();
  });

  // Build contrast map from discovered themes
  const themeContrastMap = computed<Map<string, ContrastType>>(() => {
    const map = new Map<string, ContrastType>();
    for (const theme of availableThemes.value) {
      map.set(theme.id, theme.contrast);
    }
    return map;
  });

  // Group themes by category, preserving category order based on first appearance
  const groupedThemes = computed<ThemeCategoryGroup[]>(() => {
    const categoryOrder: string[] = [];
    const groups = new Map<string, ThemeOption[]>();

    for (const theme of availableThemes.value) {
      if (!groups.has(theme.category)) {
        categoryOrder.push(theme.category);
        groups.set(theme.category, []);
      }
      groups.get(theme.category)!.push(theme);
    }

    return categoryOrder.map((category) => ({
      category,
      themes: groups.get(category)!,
    }));
  });

  const enabledTheme = useStorage<string>('ds:enabled-theme', 'dark');

  // Validate that the stored theme still exists; reset when list changes (e.g. new themes added)
  watch(
    availableThemes,
    () => {
      if (!availableThemes.value.some((t) => t.id === enabledTheme.value)) {
        enabledTheme.value = 'dark';
      }
    },
    { immediate: true }
  );

  const setTheme = (themeId: string) => {
    enabledTheme.value = themeId;
  };

  // Check if we're on a public page that should use OS preference only
  const isPublicThemePage = () => {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname;
    return (
      path.startsWith('/item-request/') || path.startsWith('/stored-view/')
    );
  };

  // Watch for theme changes and update the data-theme attribute
  // All themes use explicit data-theme attribute for consistency
  watch(
    enabledTheme,
    (newTheme) => {
      if (typeof document !== 'undefined') {
        // On public pages, don't override theme - useSystemTheme handles it
        if (!isPublicThemePage()) {
          document.documentElement.setAttribute('data-theme', newTheme);
        }
        // Increment version to trigger color rebuild after DOM update
        // Use setTimeout to ensure CSS variables are updated first
        setTimeout(() => {
          colorsVersion.value++;
        }, 0);
      }
    },
    { immediate: true }
  );

  // Computed that rebuilds colors when version changes
  const appColors = computed(() => {
    // Reference colorsVersion to make this reactive to theme changes
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    colorsVersion.value;
    return buildColorsFromCssVariables();
  });

  const contrastType = computed<ContrastType>(() => {
    return themeContrastMap.value.get(enabledTheme.value) ?? 'dark';
  });

  return {
    availableThemes,
    groupedThemes,
    enabledTheme,
    contrastType,
    appColors,
    setTheme,
  };
}

export function lightenDarkenColor(col: string, amt: number) {
  // check if rgb
  if (col.startsWith('rgb')) {
    const [r, g, b] = parseColorStringToRgb(col) ?? [0, 0, 0];
    return `rgb(${r + amt}, ${g + amt}, ${b + amt})`;
  }

  let usePound = false;

  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

export const parseColorStringToRgb = (
  color: string
): [number, number, number] | null => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = color;
  if (!ctx.fillStyle) return null; // invalid color
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
};

// ============================================================================
// Color Conversion Utilities
// ============================================================================

/**
 * Clamps a number to a range.
 */
export function clamp(n: number, min = 0, max = 255): number {
  n = Number(n);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

/**
 * Converts RGB array to hex string.
 */
export function rgbToHex(rgb: number[]): string {
  const to = (x: number) => x.toString(16).padStart(2, '0');
  return `#${to(rgb[0])}${to(rgb[1])}${to(rgb[2])}`.toUpperCase();
}

/**
 * Converts hex string to RGB array.
 * Returns null if hex is invalid.
 */
export function hexToRgb(hex: string): number[] | null {
  const h = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/**
 * Converts RGB array to HSL array.
 * Returns [hue (0-360), saturation (0-100), lightness (0-100)].
 */
export function rgbToHsl(rgb: number[]): number[] {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}

/**
 * Converts HSL array to RGB array.
 * Expects [hue (0-360), saturation (0-100), lightness (0-100)].
 */
export function hslToRgb(hsl: number[]): number[] {
  const h = ((hsl[0] % 360) + 360) % 360;
  const s = clamp(hsl[1], 0, 100) / 100;
  const l = clamp(hsl[2], 0, 100) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let rp = 0,
    gp = 0,
    bp = 0;
  if (0 <= h && h < 60) {
    rp = c;
    gp = x;
    bp = 0;
  } else if (60 <= h && h < 120) {
    rp = x;
    gp = c;
    bp = 0;
  } else if (120 <= h && h < 180) {
    rp = 0;
    gp = c;
    bp = x;
  } else if (180 <= h && h < 240) {
    rp = 0;
    gp = x;
    bp = c;
  } else if (240 <= h && h < 300) {
    rp = x;
    gp = 0;
    bp = c;
  } else {
    rp = c;
    gp = 0;
    bp = x;
  }

  return [
    clamp(Math.round((rp + m) * 255)),
    clamp(Math.round((gp + m) * 255)),
    clamp(Math.round((bp + m) * 255)),
  ];
}

/**
 * Formats HSL array as CSS hsl() string.
 */
export function formatHsl(hsl: number[]): string {
  return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
}

/**
 * Formats RGB array as CSS rgb() string.
 */
export function rgbString(rgb: number[]): string {
  return `rgb(${rgb.join(',')})`;
}

/**
 * Converts sRGB channel value to linear RGB.
 */
export function srgbToLinear(c: number): number {
  c = c / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * Calculates the relative luminance of an RGB color.
 * Used for determining contrast (WCAG).
 */
export function relativeLuminance(rgb: number[]): number {
  const R = srgbToLinear(rgb[0]);
  const G = srgbToLinear(rgb[1]);
  const B = srgbToLinear(rgb[2]);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Returns appropriate label color (dark or light) based on background luminance.
 */
export function labelColor(rgb: number[]): string {
  return relativeLuminance(rgb) > 0.35 ? 'rgb(35,32,28)' : 'rgb(210,200,185)';
}
